import React, { useContext, useRef } from 'react';
import styled from 'styled-components';
import produce from 'immer';
import { IoEyeOutline } from 'react-icons/io5';
import {
  EditorDeclarativeBlock,
  ExperienceVariant,
} from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import { ThemesModal } from '@/features/editor/widgets/shared/modals/audience/themes-modal';
import { useThemesModal } from '@/features/editor/widgets/shared/targeting-controls/use-themes-modal';
import { InlineVariantsLayout } from '@/features/editor/widgets/shared/inline-variants-layout';
import { ThemeChange } from '@/pkg/sdk';

interface ThemesAllocationCardProps {
  originalChange?: EditorDeclarativeBlock;
}

export function ThemesAllocation({
  originalChange,
}: ThemesAllocationCardProps) {
  const { experienceState, resources } = useContext(EditorContext);
  const { currentExperience, upsertEditorChange, selectTheme } =
    experienceState;
  const fromRef = useRef(null);

  const {
    themes,
    isVisible: isThemesModalVisible,
    setIsVisible: setIsThemesModalVisible,
    onClick: onShowThemesModal,
  } = useThemesModal();

  const onThemeSelect = (themeId: number) => {
    const change = produce(originalChange, (draft: EditorDeclarativeBlock) => {
      draft.block.kind = `themeTest`;
      draft.block.selector = `div`;
      draft.block.value = {
        targetThemeId: themeId,
      } as ThemeChange;
    });
    upsertEditorChange(change, false, false, themeId);
  };

  const mainTheme = resources?.themes?.find((theme) => theme.role === `main`);
  const isMainTheme = currentExperience.themeId === mainTheme.id;

  const onThemeSelectClicked = (variant: ExperienceVariant) => {
    experienceState.selectActiveVariant(variant.id);
    const targetThemeId = (variant?.changes?.[0]?.block?.value as ThemeChange)
      ?.targetThemeId;
    if (targetThemeId) {
      document.dispatchEvent(
        new CustomEvent(`SelectTheme`, {
          detail: { targetThemeId },
        }),
      );
    }
    onShowThemesModal();
  };

  return (
    <TopWrapper ref={fromRef}>
      <ThemesModal
        header="please select variation theme"
        fromRef={fromRef}
        isVisible={isThemesModalVisible}
        setIsVisible={setIsThemesModalVisible}
        onSaveCb={onThemeSelect}
        disableMainTheme
        themes={themes}
      />
      <InlineVariantsLayout
        controlDescription={
          <LiveTheme>
            <GreenDot />
            <span>Theme: ({mainTheme?.name || ``})</span>
          </LiveTheme>
        }
        controlAction={
          <Anchor onClick={() => selectTheme(mainTheme.id)}>
            <IoEyeOutline size={24} color={isMainTheme ? `black` : `#aeafb1`} />
          </Anchor>
        }
        variantDescription={(variant) => {
          const targetThemeId = (
            variant?.changes?.[0]?.block?.value as ThemeChange
          )?.targetThemeId;
          const themeName = themes?.find(
            (theme) => theme.id === targetThemeId,
          )?.name;
          return (
            <SelectThemeText
              onClick={() => onThemeSelectClicked(variant)}
              isBlue={!themeName}
            >
              {themeName ? (
                <FlexRow>
                  <GreyDot />
                  <ThemeName>{themeName}</ThemeName>
                </FlexRow>
              ) : (
                `Select Theme >`
              )}
            </SelectThemeText>
          );
        }}
        variantAction={(variant) => {
          const targetThemeId = (
            variant?.changes?.[0]?.block?.value as ThemeChange
          )?.targetThemeId;
          const isEnabled =
            experienceState?.currentExperience?.themeId === targetThemeId;
          return (
            <BtnWrapper>
              {targetThemeId && (
                <Anchor
                  onClick={() => {
                    experienceState.selectTheme(targetThemeId);
                  }}
                >
                  <IoEyeOutline
                    size={24}
                    color={isEnabled ? `black` : `#aeafb1`}
                  />
                </Anchor>
              )}
            </BtnWrapper>
          );
        }}
      />
    </TopWrapper>
  );
}

const TopWrapper = styled.div`
  .variant-edit-btn {
    display: none;
  }
`;

const FlexRow = styled.div`
  align-items: center;
  justify-content: start;
  display: flex;
`;

const GreenDot = styled.span`
  background: #2cdc55;
  content: '';
  display: block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.5rem;
`;

const LiveTheme = styled.div`
  font-family: Inter, serif;
  display: flex;
  justify-content: start;
  align-items: center;
  color: #7b8995;
  padding: 0 2rem;
  margin-top: 0.3rem;

  span {
    max-width: 12vw;
    @media (max-width: 1200px) {
      max-width: 10vw;
    }
    @media (max-width: 1000px) {
      max-width: 8vw;
    }
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    font-size: 1.2rem;
    font-weight: 500;
  }
`;

const BtnWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  opacity: ${(p: { isActive: boolean }) => (p.isActive ? `0.5` : `1`)};
  color: #9199a6;

  span {
    text-decoration: underline;
  }

  font-family: Inter, serif;
  font-size: ${(p) => (p.bg ? `1.25` : `1`)}rem;

  :hover {
    opacity: ${(p: { isActive: boolean }) => (p.isActive ? `0.5` : `0.6`)};
    cursor: ${(p: { isActive: boolean }) =>
      p.isActive ? `default` : `pointer`};
  }

  :active {
    opacity: ${(p: { isActive: boolean }) => (p.isActive ? `0.5` : `1`)};
    cursor: default;
  }
`;

const SelectThemeText = styled.p`
  font-family: Inter, serif;
  margin: 1rem 0 0;

  color: ${(p) => (p.isBlue ? `blue` : `black`)};
  text-decoration: underline;
  font-size: 1.2rem;
  font-weight: 700;

  &:hover {
    opacity: 0.8;
    cursor: pointer;
  }
`;

const GreyDot = styled.span`
  display: block;
  background: #9ea8b1;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 0.5rem;
`;

const ThemeName = styled.span`
  width: 150px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Anchor = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;
